import React from "react";
import { motion } from "framer-motion";
import { useMicrospecThemeLanguageContext } from "../../../MicrospecDynamicUI/Themes";
import { Box, Typography, Container } from "@mui/material";
import CountUp from "react-countup";
import VerifiedIcon from "@mui/icons-material/Verified";
import GroupsIcon from "@mui/icons-material/Groups";
import ScienceIcon from "@mui/icons-material/Science";
import StarIcon from "@mui/icons-material/Star";

const statIcons = [VerifiedIcon, GroupsIcon, ScienceIcon, StarIcon];
const iconColors = ["#7C3AED", "#6366F1", "#A855F7", "#EC4899"];

const StatsSection = () => {
  const { localizationProvider, dir } = useMicrospecThemeLanguageContext();

  const stats = [0, 1, 2, 3].map((i) => ({
    value: localizationProvider.translateByType({
      type: "Localized",
      text: `landing.stats.items.${i}.value`,
    }),
    suffix: localizationProvider.translateByType({
      type: "Localized",
      text: `landing.stats.items.${i}.suffix`,
    }),
    label: localizationProvider.translateByType({
      type: "Localized",
      text: `landing.stats.items.${i}.label`,
    }),
  }));

  return (
    <Box className="py-20 relative z-20" sx={{ background: "transparent" }}>
      {/* Vivid gradient separator line */}
      <svg
        className="absolute top-0 left-0 w-full h-px pointer-events-none overflow-visible"
        viewBox="0 0 1400 2"
        preserveAspectRatio="none"
      >
        <motion.line
          x1="0"
          y1="1"
          x2="1400"
          y2="1"
          stroke="url(#statLineBright)"
          strokeWidth="2"
          initial={{ pathLength: 0 }}
          whileInView={{ pathLength: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 2 }}
        />
        <defs>
          <linearGradient id="statLineBright" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="transparent" />
            <stop offset="20%" stopColor="#8B5CF6" stopOpacity="0.2" />
            <stop offset="50%" stopColor="#C084FC" stopOpacity="0.35" />
            <stop offset="80%" stopColor="#8B5CF6" stopOpacity="0.2" />
            <stop offset="100%" stopColor="transparent" />
          </linearGradient>
        </defs>
      </svg>

      <Container maxWidth="xl" dir={dir}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 px-4 md:px-8">
          {stats.map((stat, index) => {
            const Icon = statIcons[index];
            const color = iconColors[index];
            const numericValue = parseFloat(stat.value);
            console.log(
              `Stat ${index}: value=${stat.value}, numericValue=${numericValue}, isNumeric=${Number.isFinite(numericValue)}`,
            );
            if (isNaN(numericValue)) {
              console.warn(
                `Stat ${index} value is not valid number: ${stat.value}`,
              );
              console.log(stat);
            }
            const isNumeric = Number.isFinite(numericValue);
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group relative"
              >
                <div
                  className="flex flex-col items-center text-center p-8 rounded-3xl transition-all duration-500 relative overflow-hidden backdrop-blur-xl"
                  style={{
                    background: "rgba(255,255,255,0.35)",
                    border: "1px solid rgba(139,92,246,0.08)",
                    boxShadow: "0 4px 20px rgba(139,92,246,0.04)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = `0 12px 40px ${color}18, 0 0 0 1px rgba(255,255,255,0.5) inset`;
                    e.currentTarget.style.transform = "translateY(-4px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow =
                      "0 4px 20px rgba(139,92,246,0.04)";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  {/* Icon with pulse ring */}
                  <div className="relative mb-5">
                    <motion.div
                      className="absolute inset-[-8px] rounded-2xl"
                      style={{ border: `1.5px solid ${color}`, opacity: 0.15 }}
                      animate={{ scale: [1, 1.3, 1], opacity: [0.15, 0, 0.15] }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        delay: index * 0.5,
                      }}
                    />
                    <div
                      className="w-14 h-14 rounded-2xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
                      style={{
                        background: `linear-gradient(135deg, ${color}15, ${color}25)`,
                        boxShadow: `0 4px 16px ${color}12`,
                      }}
                    >
                      <Icon sx={{ fontSize: 26, color }} />
                    </div>
                  </div>

                  <Typography
                    className="text-3xl md:text-4xl font-extrabold tabular-nums mb-1"
                    sx={{
                      background: `linear-gradient(135deg, #2D1B4E, ${color})`,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {isNumeric ? (
                      <CountUp
                        end={numericValue}
                        decimals={stat.value.includes(".") ? 1 : 0}
                        duration={2.5}
                        enableScrollSpy
                        scrollSpyOnce
                        suffix={stat.suffix}
                      />
                    ) : (
                      `${stat.value}${stat.suffix}`
                    )}
                  </Typography>
                  <Typography
                    className="text-sm font-medium"
                    sx={{ color: "#6B5B7B" }}
                  >
                    {stat.label}
                  </Typography>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Container>
    </Box>
  );
};

export default StatsSection;
